import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate session ID
const generateSessionId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// Get device type from user agent
const getDeviceType = (userAgent: string): string => {
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'Tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
    return 'Mobile';
  }
  return 'Desktop';
};

// Get browser from user agent
const getBrowser = (userAgent: string): string => {
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Other';
};

// Get OS from user agent
const getOS = (userAgent: string): string => {
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('X11')) return 'Unix';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
  return 'Other';
};

let sessionId = generateSessionId();
let sessionStart = Date.now();

// Check if user has consented to analytics tracking
const hasAnalyticsConsent = (): boolean => {
  try {
    const consentStr = localStorage.getItem('cookie-consent');
    if (!consentStr) return false;
    
    const consent = JSON.parse(consentStr);
    return consent.analytics === true;
  } catch {
    return false;
  }
};

export const useAnalytics = () => {
  const location = useLocation();
  const pageStartTime = useRef(Date.now());

  useEffect(() => {
    const trackPageView = async () => {
      // Check for analytics consent first
      if (!hasAnalyticsConsent()) {
        console.log('Analytics tracking skipped - no user consent');
        return;
      }

      try {
        const userAgent = navigator.userAgent;
        
        // Check authentication status (optional now - we track all visitors)
        const { data: { session } } = await supabase.auth.getSession();
        
        const analyticsData = {
          page_path: location.pathname,
          referrer: document.referrer || null,
          user_agent: userAgent,
          device_type: getDeviceType(userAgent),
          browser: getBrowser(userAgent),
          os: getOS(userAgent),
          session_id: sessionId,
          user_id: session?.user?.id || null, // null for anonymous visitors
          is_unique_visitor: !sessionStorage.getItem('visited'),
          duration_seconds: 0,
        };

        // Mark as visited
        if (!sessionStorage.getItem('visited')) {
          sessionStorage.setItem('visited', 'true');
        }

        // Insert analytics data - now works for all visitors
        await supabase.from('website_analytics').insert(analyticsData);
        
        pageStartTime.current = Date.now();
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
    };

    trackPageView();

    // Track page duration on unmount
    return () => {
      // Only track duration if user has consented
      if (!hasAnalyticsConsent()) return;

      const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
      if (duration > 0 && duration < 3600) { // Max 1 hour to avoid outliers
        const updateDuration = async () => {
          try {
            await supabase
              .from('website_analytics')
              .update({ duration_seconds: duration })
              .eq('session_id', sessionId)
              .eq('page_path', location.pathname)
              .order('created_at', { ascending: false })
              .limit(1);
          } catch (error) {
            console.error('Error updating duration:', error);
          }
        };
        updateDuration();
      }
    };
  }, [location.pathname]);

  // Update session every 30 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - sessionStart > 30 * 60 * 1000) {
        sessionId = generateSessionId();
        sessionStart = Date.now();
      }
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);
};